/**
 * Unifies and deduplicates enrolled courses from userEnrolledCohorts (/user/enrollments/cohorts)
 * and progressData (/user/progress), ensuring that duplicate records across endpoints or
 * cohorts collapse correctly while preserving rich thumbnails, cohort slugs, and assignment breakdowns.
 */
export const deduplicateEnrollments = (userEnrolledCohorts, progressData) => {
  const listA = Array.isArray(userEnrolledCohorts) ? userEnrolledCohorts : [];
  const listB = Array.isArray(progressData) ? progressData : [];

  const map = new Map();

  const getCanonicalKey = (item, fallbackIdx) => {
    const id = item.id || item.enrollment_id;
    if (id) return `enr_${id}`;
    const cohortId = item.cohort?.id || item.cohort_id;
    if (cohortId) return `cohort_${cohortId}`;
    const cohortSlug = item.cohort?.slug;
    if (cohortSlug) return `slug_${cohortSlug}`;
    const courseId = item.course?.id || item.course_id;
    if (courseId) return `course_${courseId}`;
    return `idx_${fallbackIdx}`;
  };

  const findExistingKey = (item) => {
    const itemEnrollId = String(item.enrollment_id || "").trim();
    const itemId = String(item.id || "").trim();
    const itemCohortId = String(item.cohort?.id || item.cohort_id || "").trim();
    const itemCohortSlug = item.cohort?.slug ? String(item.cohort.slug).trim().toLowerCase() : "";
    const itemCourseId = String(item.course?.id || item.course_id || item.cohort?.course?.id || "").trim();

    for (const [key, existing] of map.entries()) {
      const existEnrollId = String(existing.enrollment_id || "").trim();
      const existId = String(existing.id || "").trim();
      const existCohortId = String(existing.cohort?.id || existing.cohort_id || "").trim();
      const existCohortSlug = existing.cohort?.slug ? String(existing.cohort.slug).trim().toLowerCase() : "";
      const existCourseId = String(existing.course?.id || existing.course_id || existing.cohort?.course?.id || "").trim();

      // 1. Direct ID match across id and enrollment_id
      if (
        (itemId && existId && itemId === existId) ||
        (itemId && existEnrollId && itemId === existEnrollId) ||
        (itemEnrollId && existId && itemEnrollId === existId) ||
        (itemEnrollId && existEnrollId && itemEnrollId === existEnrollId)
      ) {
        return key;
      }

      // 2. Cohort ID match
      if (itemCohortId && existCohortId && itemCohortId === existCohortId) {
        return key;
      }

      // 3. Cohort slug match
      if (itemCohortSlug && existCohortSlug && itemCohortSlug === existCohortSlug) {
        return key;
      }

      // 4. Course match (when neither has a separate cohort)
      if (
        !itemCohortId &&
        !existCohortId &&
        itemCourseId &&
        existCourseId &&
        itemCourseId === existCourseId
      ) {
        return key;
      }
    }

    return null;
  };

  listA.forEach((item, index) => {
    const matchedKey = findExistingKey(item);
    const key = matchedKey || getCanonicalKey(item, `a_${index}`);
    if (map.has(key)) {
      const existing = map.get(key);
      map.set(key, {
        ...existing,
        ...item,
        cohort: existing.cohort || item.cohort,
        course: existing.course || item.course,
        class_option: existing.class_option || item.class_option,
        duration: existing.duration || item.duration || existing.cohort?.duration,
      });
    } else {
      map.set(key, { ...item });
    }
  });

  listB.forEach((item, index) => {
    const matchedKey = findExistingKey(item);
    if (matchedKey) {
      const existing = map.get(matchedKey);
      map.set(matchedKey, {
        ...existing,
        ...item,
        // Preserve rich objects from listA
        cohort: existing.cohort || item.cohort,
        course: existing.course || item.course,
        class_option: existing.class_option || item.class_option,
        duration: existing.duration || item.duration || existing.cohort?.duration,
        progress: item.progress !== undefined ? item.progress : (existing.progress || 0),
        breakdown: item.breakdown || existing.breakdown,
        enrollment_id: existing.id || item.enrollment_id || existing.enrollment_id,
        id: existing.id || item.id || item.enrollment_id,
      });
    } else {
      const key = getCanonicalKey(item, `b_${index}`);
      map.set(key, {
        id: item.enrollment_id || item.id,
        ...item,
      });
    }
  });

  const result = Array.from(map.values());
  return result.length > 0 ? result : (listA.length > 0 ? listA : listB);
};
